import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District, DistrictAiVerificationConfig } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useVerificationTemplates } from '../verification-admin';

/**
 * AI 検証設定 panel (Sprint BG-DataSettings 15、旧 v1 `District.aiVerification` 編集セクション踏襲)。
 *
 * 旧 v1 `old/apps/admin/src/pages/DistrictDetailPage.tsx` (line 760〜846 の「AI 検証機能」section 踏襲)。
 *
 * 踏襲した点:
 *   - enabled checkbox + 「自由検証を許可」 checkbox
 *   - activeTemplateId pull-down (地区別 `/districts/{id}/aiVerificationTemplates` から取得)
 *     null = 自動選択 (最新の published) — 旧と同じ
 *   - AI 検証画面への inline link (旧 verification template 編集ページへ)
 *
 * 変えた点 (v2):
 *   - shared-types `DistrictAiVerificationConfig` 型を使用 (旧は admin 内 local type)
 *   - `consensusThreshold` / `allowMultipleSubmissions` の編集 UI は本 sprint scope 外
 *     (forward-compatible に保存値は維持)
 *
 * v2 では `District.aiVerification` field は shared-types に正式存在するため
 * 型安全に setDoc 可能 (DistrictAiDialoguePanel と異なる)。
 */

/** pure: District → 現在 config の初期 draft (未設定 → defaults)。 */
export function readAiVerificationFromDistrict(d: District): DistrictAiVerificationConfig {
  const cur = d.aiVerification;
  return {
    enabled: cur?.enabled ?? false,
    activeTemplateId: cur?.activeTemplateId ?? null,
    allowFreeVerification: cur?.allowFreeVerification ?? true,
    ...(cur?.consensusThreshold !== undefined
      ? { consensusThreshold: cur.consensusThreshold }
      : {}),
    ...(cur?.allowMultipleSubmissions !== undefined
      ? { allowMultipleSubmissions: cur.allowMultipleSubmissions }
      : {}),
  };
}

/** pure: payload を build (空 string activeTemplateId → null 正規化)。 */
export function buildAiVerificationPayload(
  draft: DistrictAiVerificationConfig,
): DistrictAiVerificationConfig {
  const cleaned: DistrictAiVerificationConfig = {
    enabled: !!draft.enabled,
    activeTemplateId: draft.activeTemplateId || null,
    allowFreeVerification: draft.allowFreeVerification !== false,
  };
  if (draft.consensusThreshold !== undefined) cleaned.consensusThreshold = draft.consensusThreshold;
  if (draft.allowMultipleSubmissions !== undefined)
    cleaned.allowMultipleSubmissions = draft.allowMultipleSubmissions;
  return cleaned;
}

export interface DistrictAiVerificationPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictAiVerificationPanel({
  district,
  uid,
  canEdit,
}: DistrictAiVerificationPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DistrictAiVerificationConfig>(() =>
    readAiVerificationFromDistrict(district),
  );
  const [saving, setSaving] = useState(false);
  const templatesState = useVerificationTemplates(district.id);
  const templates = templatesState.status === 'ready' ? templatesState.items : [];

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = buildAiVerificationPayload(draft);
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          aiVerification: payload,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'AI 検証設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-ai-verification-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">✅ AI 検証設定</h3>
      <p className="text-xs text-slate-500 mb-3">
        参加者にAI解析結果の妥当性を回答してもらう機能。質問テンプレと割当セットと連動します。
      </p>

      <label className="flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          disabled={!canEdit || saving}
          className="h-4 w-4"
          data-testid="ai-verification-enabled"
        />
        <span className="text-sm">この地区で AI 検証機能を有効化</span>
      </label>

      {draft.enabled && (
        <div className="ml-6 space-y-3 mb-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">使用する質問テンプレート</span>
            <select
              value={draft.activeTemplateId ?? ''}
              onChange={(e) => setDraft({ ...draft, activeTemplateId: e.target.value || null })}
              disabled={!canEdit || saving}
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
              data-testid="ai-verification-template-select"
            >
              <option value="">— 自動選択 (最新の published) —</option>
              {templatesState.status === 'loading' && (
                <option value="" disabled>
                  読込中…
                </option>
              )}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName} (v{t.version}) [{t.status}]
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              「自動選択」を選ぶと published 状態のテンプレが 1
              件あればそれを使用。複数ある場合は明示指定推奨。
            </p>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.allowFreeVerification}
              onChange={(e) => setDraft({ ...draft, allowFreeVerification: e.target.checked })}
              disabled={!canEdit || saving}
              className="h-4 w-4"
              data-testid="ai-verification-allow-free"
            />
            <span className="text-sm">自由検証を許可 (ユーザーが任意の AI 解析済投稿を検証可)</span>
          </label>

          <Link
            to={`/districts/${district.id}/verification/templates`}
            className="inline-block px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            検証テンプレート編集を開く →
          </Link>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="ai-verification-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
